import React, { useCallback } from "react";
import { SubToPages_pages } from "../../../api/SubToPages";
import SyllableComponent from "../components/SyllableComponent";
import {
  useCreateSyllableFromPosition,
  useDeleteSyllableFromPosition,
  useUnsetMuteFromPosition,
  setMuteFromPosition as useSetMuteFromPosition,
  useCreateColorationFromPosition,
  useDeleteColorationFromPosition,
  useUnsetPhonemeFromPosition,
  useSetPhonemeFromPosition,
} from "../../../api/api";
import { SubToProject_project } from "../../../api/__generated__/SubToProject";
import { SelectedTextPosition } from "../../EditingModes/Formatting/components/FormattingComp/FormattingComp";
import {
  AddExtendedMetadataCustomColoration,
  ExtendedPhonemeKind,
} from "../../../../../../../__generated__/globalTypes";

type Props = {
  allPages: SubToPages_pages[];
  project: SubToProject_project;
};

export function PaginatedSyllable({ allPages, project }: Props) {
  const projectId = project.id;
  const stringVersion = allPages[0]?.stringVersion;
  const [applyCreateSyllableFromPosition] = useCreateSyllableFromPosition();
  const [applyDeleteSyllableFromPosition] = useDeleteSyllableFromPosition();
  const [applySetMutedFromPosition] = useSetMuteFromPosition();
  const [applyUnsetMuteFromPosition] = useUnsetMuteFromPosition();
  const [applySetPhonemeFromPosition] = useSetPhonemeFromPosition();
  const [applyUnsetPhonemeFromPosition] = useUnsetPhonemeFromPosition();
  const [applyCreateColorationFromPosition] = useCreateColorationFromPosition();
  const [applyDeleteColorationFromPosition] = useDeleteColorationFromPosition();

  const removePhoneme = useCallback(
    (selectedTextPosition: SelectedTextPosition) =>
      applyUnsetPhonemeFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      }),
    [applyUnsetPhonemeFromPosition, projectId, stringVersion],
  );

  const assignPhoneme = useCallback(
    (selectedTextPosition: SelectedTextPosition, phoneme: ExtendedPhonemeKind) =>
      applySetPhonemeFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
          kind: phoneme,
        },
      }),
    [applySetPhonemeFromPosition, projectId, stringVersion],
  );

  const removeCustomColor = useCallback(
    (selectedTextPosition: SelectedTextPosition) =>
      applyDeleteColorationFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      }),
    [applyDeleteColorationFromPosition, projectId, stringVersion],
  );

  const assignCustomColor = useCallback(
    (selectedTextPosition: SelectedTextPosition, coloration: AddExtendedMetadataCustomColoration) =>
      applyCreateColorationFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
          newColoration: coloration,
        },
      }),
    [applyCreateColorationFromPosition, projectId, stringVersion],
  );

  const removeSyllable = useCallback(
    async (selectedTextPosition: SelectedTextPosition) => {
      await applyDeleteSyllableFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      });
    },
    [applyDeleteSyllableFromPosition, projectId, stringVersion],
  );

  const mergeSyllables = useCallback(
    async (selectedTextPosition: SelectedTextPosition) => {
      await applyCreateSyllableFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      });
    },
    [applyCreateSyllableFromPosition, projectId, stringVersion],
  );

  const muteLetters = useCallback(
    (selectedTextPosition: SelectedTextPosition) => {
      applySetMutedFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      });
    },
    [applySetMutedFromPosition, projectId, stringVersion],
  );

  const unmuteLetters = useCallback(
    (selectedTextPosition: SelectedTextPosition) => {
      applyUnsetMuteFromPosition({
        variables: {
          projectId,
          size: selectedTextPosition.size,
          start: selectedTextPosition.start,
          stringVersion,
        },
      });
    },
    [applyUnsetMuteFromPosition, projectId, stringVersion],
  );

  const customColors = Object.entries(project.customColorationConfig || {}).map(([key, value]) => ({
    key, ...(JSON.parse(value as string) as { label: string; color: string }),
  }));
  const componentProps = { removeSyllable, merge: mergeSyllables, mute: muteLetters,
    unmute: unmuteLetters, removePhoneme, assignPhoneme, customColors, removeCustomColor,
    assignCustomColor, projectId, allPages,
  };
  return <SyllableComponent componentProps={componentProps} pages={allPages} projectId={projectId} />;
}
